// =============================================================================
// BALANCE OBSERVATION SERVICE
// =============================================================================
//
// Aggregeert data voor neutrale observatie van patronen.
// GEEN interpretatie, GEEN scores, GEEN labels.
//
// =============================================================================

import { Goal, Milestone, Action, SparringSession } from '../types';

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export interface ActionRhythm {
  totalActions: number;
  completedActions: number;
  actionsLast7Days: number;
  actionsLast30Days: number;
  firstActionDate: number | null;
  lastActionDate: number | null;
  lastCompletedDate: number | null;
  daysSinceLastAction: number | null;
}

export interface StructureObservation {
  totalMilestones: number;
  actionsWithMilestone: number;
  orphanActions: number;
  milestonesWithActions: number;
  milestonesWithoutActions: number;
}

export interface ExternalObservation {
  sparringSessions: number;
  reflectionSessions: number;
  lastSparringDate: number | null;
  lastReflectionDate: number | null;
}

export interface BalanceData {
  rhythm: ActionRhythm;
  structure: StructureObservation;
  external: ExternalObservation;
  hasMinimalData: boolean;  // True if < 3 total actions
  hasLongInactivity: boolean;  // True if > 30 days since last action
  // ⭐ NEW: Pattern categories for AI context
  focusDirection: 'intern' | 'extern' | 'balanced';
  processApproach: 'plannen' | 'afronding' | 'balanced';
}

// -----------------------------------------------------------------------------
// DATA AGGREGATION
// -----------------------------------------------------------------------------

/**
 * Aggregate action rhythm data
 */
function aggregateActionRhythm(actions: Action[]): ActionRhythm {
  const now = Date.now();
  const sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);

  const completed = actions.filter(a => a.completed);
  const recent7 = actions.filter(a => a.createdAt > sevenDaysAgo);
  const recent30 = actions.filter(a => a.createdAt > thirtyDaysAgo);

  const actionDates = actions.map(a => a.createdAt).sort((a, b) => a - b);

  const firstActionDate = actionDates.length > 0 ? actionDates[0] : null;
  const lastActionDate = actionDates.length > 0 ? actionDates[actionDates.length - 1] : null;
  // Note: Action type has no completedAt timestamp, only completed boolean
  const lastCompletedDate = null;

  const daysSinceLastAction = lastActionDate
    ? Math.floor((now - lastActionDate) / (24 * 60 * 60 * 1000))
    : null;

  return {
    totalActions: actions.length,
    completedActions: completed.length,
    actionsLast7Days: recent7.length,
    actionsLast30Days: recent30.length,
    firstActionDate,
    lastActionDate,
    lastCompletedDate,
    daysSinceLastAction,
  };
}

/**
 * Aggregate structure data
 */
function aggregateStructure(milestones: Milestone[], actions: Action[]): StructureObservation {
  const orphanActions = actions.filter(a => !a.milestoneId);
  const actionsWithMilestone = actions.filter(a => a.milestoneId);

  const milestonesWithActions = milestones.filter(m =>
    actions.some(a => a.milestoneId === m.id)
  );
  const milestonesWithoutActions = milestones.filter(m =>
    !actions.some(a => a.milestoneId === m.id)
  );

  return {
    totalMilestones: milestones.length,
    actionsWithMilestone: actionsWithMilestone.length,
    orphanActions: orphanActions.length,
    milestonesWithActions: milestonesWithActions.length,
    milestonesWithoutActions: milestonesWithoutActions.length,
  };
}

/**
 * Aggregate external interaction data
 */
function aggregateExternal(sessions: SparringSession[]): ExternalObservation {
  const reflectionSessions = sessions.filter(s => s.mode === 'reflection');
  const otherSessions = sessions.filter(s => s.mode !== 'reflection');

  const allDates = sessions
    .map(s => s.createdAt)
    .sort((a, b) => b - a);

  const reflectionDates = reflectionSessions
    .map(s => s.createdAt)
    .sort((a, b) => b - a);

  const otherDates = otherSessions
    .map(s => s.createdAt)
    .sort((a, b) => b - a);

  return {
    sparringSessions: otherSessions.length,
    reflectionSessions: reflectionSessions.length,
    lastSparringDate: otherDates.length > 0 ? otherDates[0] : null,
    lastReflectionDate: reflectionDates.length > 0 ? reflectionDates[0] : null,
  };
}

// -----------------------------------------------------------------------------
// PATTERN CATEGORIZATION
// -----------------------------------------------------------------------------

/**
 * Determine focus direction: intern, extern, or balanced
 * Based on external activity (sparring/reflection) vs internal actions
 */
function categorizeFocusDirection(rhythm: ActionRhythm, external: ExternalObservation): 'intern' | 'extern' | 'balanced' {
  // Not enough data
  if (rhythm.totalActions < 3) {
    return 'balanced';
  }

  const externalCount = external.sparringSessions + external.reflectionSessions;
  const hasSignificantExternal = externalCount >= 2;
  const hasMinimalExternal = externalCount === 1;

  // Extern-gericht: veel externe validatie
  if (hasSignificantExternal && rhythm.totalActions >= 3) {
    return 'extern';
  }

  // Intern-gericht: veel acties, weinig externe validatie
  if (rhythm.totalActions >= 5 && !hasSignificantExternal) {
    return 'intern';
  }

  // Balanced: mix of both
  if (hasMinimalExternal && rhythm.totalActions >= 3) {
    return 'balanced';
  }

  // Default: balanced
  return 'balanced';
}

/**
 * Determine process approach: plannen, afronding, or balanced
 * Based on completion rate and milestone/action ratio
 */
function categorizeProcessApproach(rhythm: ActionRhythm, structure: StructureObservation): 'plannen' | 'afronding' | 'balanced' {
  // Not enough data
  if (structure.totalMilestones < 2 || rhythm.totalActions < 3) {
    return 'balanced';
  }

  const completionRate = rhythm.totalActions > 0 
    ? rhythm.completedActions / rhythm.totalActions 
    : 0;

  // Afronding: high completion rate
  if (completionRate >= 0.6) {
    return 'afronding';
  }

  // Plannen: many milestones, low completion
  if (structure.totalMilestones >= 3 && completionRate < 0.3) {
    return 'plannen';
  }

  // Balanced: moderate completion
  if (completionRate >= 0.4 && completionRate < 0.6) {
    return 'balanced';
  }

  // Default based on completion rate
  return completionRate < 0.4 ? 'plannen' : 'balanced';
}

// -----------------------------------------------------------------------------
// MAIN AGGREGATION
// -----------------------------------------------------------------------------

/**
 * Aggregate all balance data for a goal
 * 
 * Returns null if there is long inactivity (> 30 days) - component should hide
 */
export function aggregateBalanceData(
  goal: Goal,
  milestones: Milestone[],
  actions: Action[],
  sessions: SparringSession[]
): BalanceData | null {
  const rhythm = aggregateActionRhythm(actions);
  const structure = aggregateStructure(milestones, actions);
  const external = aggregateExternal(sessions);

  // E. Lange inactiviteit (> 30 dagen) → return null (component verbergen)
  const hasLongInactivity = rhythm.daysSinceLastAction !== null && rhythm.daysSinceLastAction > 30;
  if (hasLongInactivity && rhythm.totalActions > 0) {
    return null;  // Component moet verbergen
  }

  // A/B. Check minimal data (< 3 actions)
  const hasMinimalData = rhythm.totalActions < 3;

  // ⭐ NEW: Categorize patterns for AI context
  const focusDirection = categorizeFocusDirection(rhythm, external);
  const processApproach = categorizeProcessApproach(rhythm, structure);

  return {
    rhythm,
    structure,
    external,
    hasMinimalData,
    hasLongInactivity,
    focusDirection,
    processApproach,
  };
}
